import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { AsymptotesParams } from './asymptotes.model';

/**
 * Creates asymptote lines for functions.
 * Supports both horizontal and vertical asymptotes.
 *
 * For functions like logarithmic, exponential, and tangent-fn,
 * asymptotes can be specified explicitly or detected from function parameters.
 *
 * Asymptotes are reactive - they update when parent function params change.
 */
export class AsymptotesCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'asymptotes';

  private readonly maxVerticalAsymptotes = 10;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as AsymptotesParams | undefined;
    if (!params?.of) {
      throw new Error('Asymptotes feature requires params.of to specify the parent function');
    }
    const parentElement = context.elements.get(params.of);
    if (!parentElement) {
      throw new Error(`Parent element ${params.of} not found`);
    }

    const objects: GeometryElement[] = [];

    // ------------------------------------------------
    // HORIZONTAL ASYMPTOTE (reactive)
    // ------------------------------------------------
    const horizontalStyle = resolveFeatureStyle('line', this.featureType, context, display, ['horizontal']);
    const horizontalVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['horizontal']);

    if (this.hasHorizontalAsymptote(parentElement.type) || params.horizontal !== undefined) {
      const getHorizontalY = () => {
        if (params.horizontal !== undefined) {
          return params.horizontal;
        }
        const p = parentElement.getParams?.() ?? {};
        return this.detectHorizontalAsymptote(parentElement.type, p) ?? 0;
      };

      const hLineResult = elementFactory.line({
        a: [-1000, getHorizontalY],
        b: [1000, getHorizontalY],
        style: {
          ...horizontalStyle,
          visible: horizontalVisible,
          straightFirst: true,
          straightLast: true,
        },
      });
      objects.push(hLineResult.line);
      // Hide vertices - asymptotes don't need visible endpoints
      hLineResult.a.setAttribute({ visible: false });
      hLineResult.b.setAttribute({ visible: false });
    }

    // ------------------------------------------------
    // VERTICAL ASYMPTOTES (reactive)
    // ------------------------------------------------
    const verticalStyle = resolveFeatureStyle('line', this.featureType, context, display, ['vertical']);
    const verticalVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['vertical']);

    // For logarithmic: single vertical asymptote at x = h
    if (parentElement.type === 'logarithmic' || params.vertical !== undefined) {
      if (params.vertical !== undefined) {
        // Explicit vertical asymptotes
        for (const xValue of params.vertical.slice(0, this.maxVerticalAsymptotes)) {
          const vLineResult = elementFactory.line({
            a: [xValue, -1000],
            b: [xValue, 1000],
            style: {
              ...verticalStyle,
              visible: verticalVisible,
              straightFirst: true,
              straightLast: true,
            },
          });
          objects.push(vLineResult.line);
          vLineResult.a.setAttribute({ visible: false });
          vLineResult.b.setAttribute({ visible: false });
        }
      } else if (parentElement.type === 'logarithmic') {
        // Reactive vertical asymptote for logarithmic at x = h
        const getLogAsymptoteX = () => {
          const p = parentElement.getParams?.() ?? {};
          return p['h'] ?? 0;
        };

        const vLineResult = elementFactory.line({
          a: [getLogAsymptoteX, -1000],
          b: [getLogAsymptoteX, 1000],
          style: {
            ...verticalStyle,
            visible: verticalVisible,
            straightFirst: true,
            straightLast: true,
          },
        });
        objects.push(vLineResult.line);
        vLineResult.a.setAttribute({ visible: false });
        vLineResult.b.setAttribute({ visible: false });
      }
    }

    // For tangent-fn: multiple vertical asymptotes that depend on b and c
    // These are more complex because the number can change based on viewport
    // For now, create a fixed set that updates position reactively
    if (parentElement.type === 'tangent-fn') {
      const bbox = board.getBoundingBox();
      const [xMin, , xMax] = bbox;

      // Create asymptote lines for positions within the viewport
      // We create a fixed number and update their positions reactively
      for (let i = 0; i < this.maxVerticalAsymptotes; i++) {
        const index = i;
        const getAsymptoteX = () => {
          const p = parentElement.getParams?.() ?? {};
          const b = p['b'] ?? 1;
          const c = p['c'] ?? 0;
          // tan(b(x-c)) has vertical asymptotes at b(x-c) = π/2 + nπ
          // x = c + (π/2 + nπ)/b
          const period = Math.PI / b;
          const firstAsymptote = c + Math.PI / (2 * b);
          // Calculate which asymptote this index represents
          const startN = Math.floor((xMin - firstAsymptote) / period);
          const x = firstAsymptote + (startN + index) * period;
          return x;
        };

        // Check if this asymptote is visible
        const isVisible = () => {
          const x = getAsymptoteX();
          return x >= xMin && x <= xMax && verticalVisible !== false;
        };

        const vLineResult = elementFactory.line({
          a: [getAsymptoteX, -1000],
          b: [getAsymptoteX, 1000],
          style: {
            ...verticalStyle,
            visible: isVisible(),
            straightFirst: true,
            straightLast: true,
          },
        });
        objects.push(vLineResult.line);
        vLineResult.a.setAttribute({ visible: false });
        vLineResult.b.setAttribute({ visible: false });
      }
    }

    return {
      id,
      type: 'asymptotes',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }

  private hasHorizontalAsymptote(type: string): boolean {
    return type === 'exponential';
  }

  private detectHorizontalAsymptote(type: string, params: Record<string, number>): number | undefined {
    switch (type) {
      case 'exponential':
        return params['k'] ?? 0;
      default:
        return undefined;
    }
  }
}
